import React from 'react';
import './DataRow.css';

export interface DataRowProps extends React.HTMLAttributes<HTMLDivElement> {
  leading?: React.ReactNode;
  title: React.ReactNode;
  subtitle?: React.ReactNode;
  description?: React.ReactNode;
  amount?: React.ReactNode;
  status?: React.ReactNode;
  meta?: React.ReactNode;
  trailing?: React.ReactNode;
  footer?: React.ReactNode;
  selectedControl?: React.ReactNode;
  onClick?: () => void;
  density?: 'compact' | 'default';
}

export const DataRow: React.FC<DataRowProps> = ({
  leading,
  title,
  subtitle,
  description,
  amount,
  status,
  meta,
  trailing,
  footer,
  selectedControl,
  onClick,
  density = 'default',
  className = '',
  onKeyDown,
  role,
  tabIndex,
  ...props
}) => {
  const classNames = [
    'ui-data-row',
    `ui-data-row--${density}`,
    onClick ? 'ui-data-row--clickable' : '',
    className,
  ].filter(Boolean).join(' ');

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    onKeyDown?.(event);
    if (event.defaultPrevented || !onClick) {
      return;
    }
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onClick();
    }
  };

  return (
    <div
      className={classNames}
      role={onClick ? role || 'button' : role}
      tabIndex={onClick ? tabIndex ?? 0 : tabIndex}
      onClick={onClick}
      onKeyDown={handleKeyDown}
      {...props}
    >
      {selectedControl ? <div className="ui-data-row__select">{selectedControl}</div> : null}
      {leading ? <div className="ui-data-row__leading">{leading}</div> : null}

      <div className="ui-data-row__main">
        <div className="ui-data-row__title">{title}</div>
        {subtitle ? <div className="ui-data-row__subtitle">{subtitle}</div> : null}
        {description ? <div className="ui-data-row__description">{description}</div> : null}
      </div>

      <div className="ui-data-row__aside">
        {amount ? <div className="ui-data-row__amount">{amount}</div> : null}
        {status ? <div className="ui-data-row__status">{status}</div> : null}
        {meta ? <div className="ui-data-row__meta">{meta}</div> : null}
      </div>

      {trailing ? <div className="ui-data-row__trailing">{trailing}</div> : null}
      {footer ? <div className="ui-data-row__footer">{footer}</div> : null}
    </div>
  );
};

DataRow.displayName = 'DataRow';
